import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { OperadorParaDragDrop } from '../../../services/asignacionesUsuarioOperadorService';

// ===============================
// TIPOS
// ===============================

interface DraggableOperatorProps {
  operador: OperadorParaDragDrop;
  isInPool?: boolean; // Si está en el pool de disponibles
  sourceUserId?: number; // ID del usuario si está asignado
}

// ===============================
// COMPONENTE
// ===============================

export function DraggableOperator({ operador, isInPool = false, sourceUserId }: DraggableOperatorProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `operator-${operador.id}`,
    data: {
      type: 'operator',
      operador,
      sourceUserId,
      isFromPool: isInPool,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  // Icono según estado del operador
  const getOperatorIcon = (estado: string) => {
    switch (estado.toUpperCase()) {
      case 'ACTIVO':
        return '👨‍💼';
      case 'INACTIVO':
        return '👤';
      case 'LICENCIA':
        return '🏥';
      case 'VACACIONES':
        return '🏖️';
      default:
        return '👨‍🔧';
    }
  };

  // Obtener iniciales del operador
  const getInitials = (nombre: string | null, descripcion: string) => {
    const fullName = nombre || descripcion;
    return fullName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        group relative bg-white rounded-lg border-2 shadow-sm p-3 cursor-grab active:cursor-grabbing
        transition-all duration-200 hover:shadow-md
        ${isDragging 
          ? 'border-blue-400 shadow-lg scale-105 z-50' 
          : isInPool 
            ? 'border-gray-200 hover:border-blue-300' 
            : 'border-green-200 hover:border-green-300'
        }
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
    >
      {/* Indicador de dragging */}
      {isDragging && (
        <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
          ↗
        </div>
      )}

      {/* Contenido de la tarjeta */}
      <div className="flex items-center space-x-3">
        {/* Avatar del operador */}
        <div className="relative">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
            {getInitials(operador.nombre_completo, operador.descripcion)}
          </div>
          <div className="absolute -bottom-1 -right-1 text-lg">
            {getOperatorIcon(operador.estado)}
          </div>
        </div>

        {/* Información */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {operador.nombre_completo || operador.descripcion}
            </h4>
            <span className="text-xs text-gray-500 ml-2">
              #{operador.codigo}
            </span>
          </div>
          
          <div className="flex items-center justify-between mt-1">
            <span className={`
              inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
              ${operador.estado.toUpperCase() === 'ACTIVO' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
              }
            `}>
              {operador.estado}
            </span>
            
            {operador.rut && (
              <span className="text-xs text-gray-400">
                {operador.rut}
              </span>
            )}
          </div>
        </div>

        {/* Indicador de drag handle */}
        <div className="opacity-30 group-hover:opacity-60 transition-opacity">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      </div>

      {/* Información adicional al hacer hover */}
      <div className="absolute z-10 bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
          {operador.nombre_completo || operador.descripcion}
          {operador.rut && ` • ${operador.rut}`}
        </div>
      </div>
    </div>
  );
}
